import { Link } from '@tanstack/react-router';

import type { ClinicRelationship } from '@/entities/business-relationship/api/businessRelationship.api';
import {
  getStatusBadgeStyle,
  type RelationshipGroup,
} from '@/entities/business-relationship/lib/statusView';
import { Badge } from '@/shared/components/ui/badge';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { cn } from '@/shared/lib/utils';
import { formatShortYm } from '@/shared/utils/format';

/** 지난 달 · 이번 달 정산월 장수 — "26.06 정산 3장" (Figma 440:1151) */
const UploadCounts = ({
  item,
  highlightZero,
}: {
  item: ClinicRelationship;
  highlightZero: boolean;
}) => {
  const rows = [
    { ym: item.lastMonth, count: item.lastMonthPrescriptionCount },
    { ym: item.thisMonth, count: item.thisMonthPrescriptionCount },
  ].filter((r) => !!r.ym);
  return (
    <div className="flex flex-wrap items-center gap-x-8 whitespace-nowrap">
      {rows.map((r) => (
        <span key={r.ym} className="flex items-center gap-2">
          <span className="text-body-15-r text-fg-tertiary">
            {formatShortYm(r.ym)} 정산
          </span>
          <span
            className={cn(
              'text-body-15-sb text-fg-primary',
              // 0장 강조는 등록을 재촉하는 용도라 거래 중에만 쓴다
              highlightZero && r.count === 0 && 'text-fg-error',
            )}
          >
            {r.count}장
          </span>
        </span>
      ))}
    </div>
  );
};

/**
 * Figma 거래처_리스트 (440:1174) — 좌측 목록 한 칸.
 * - 거래 중(440:1153 · 선택 440:1175 · 호버 712:1858): 병의원명 + 정산월 장수
 * - 신청(886:12817 · 886:12826): 병의원명 + 상태 배지 한 줄
 * - 종료 · 거절(886:12835 · 886:12844): 병의원명 + 상태 배지 / 정산월 장수 (조회만)
 * 선택 = primary-100 배경 + 이름 Bold 파랑, 호버 = 회색 배경
 */
export const AccountListItem = ({
  item,
  group,
  selected,
}: {
  item: ClinicRelationship;
  group: RelationshipGroup;
  selected: boolean;
}) => {
  const badgeStyle = getStatusBadgeStyle(item.statusView);
  const name = (
    <span
      className={cn(
        'min-w-0',
        selected
          ? 'text-subtitle-16-b text-fg-accent'
          : 'text-body-16-sb text-fg-primary',
      )}
    >
      {item.clinicName}
    </span>
  );

  return (
    <Link
      to={ROUTE_PATHS.ACCOUNT_DETAIL}
      params={{ relationshipId: String(item.relationshipId) }}
      aria-current={selected ? 'page' : undefined}
      className={cn(
        'flex rounded-20 p-4 transition-colors outline-none focus-visible:shadow-[0_0_0_3px_var(--color-brand-200)]',
        selected ? 'bg-brand-100' : 'hover:bg-surface-grey',
        group === 'applying'
          ? 'items-center justify-between gap-2'
          : 'flex-col gap-2',
      )}
    >
      {group === 'applying' ? (
        <>
          {name}
          <Badge {...badgeStyle} className="shrink-0">
            {item.statusView}
          </Badge>
        </>
      ) : (
        <>
          <span className="flex items-start justify-between gap-2">
            {name}
            {group === 'closed' && (
              <Badge {...badgeStyle} className="shrink-0">
                {item.statusView}
              </Badge>
            )}
          </span>
          <UploadCounts item={item} highlightZero={group === 'ongoing'} />
        </>
      )}
    </Link>
  );
};
